<template>
  <DashboardLayout :title="$t('upgrade.redeem.title')">
    <div class="mx-auto w-full max-w-2xl flex flex-col gap-6">
      <!-- Page header -->
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-xl bg-primary-600/15 border border-primary-800 flex items-center justify-center flex-shrink-0">
          <TicketIcon class="w-6 h-6 text-primary-300" />
        </div>
        <div class="min-w-0">
          <h1 class="text-text-primary text-[28px] font-medium leading-tight">{{ $t('upgrade.redeem.title') }}</h1>
          <p class="text-sm text-text-secondary mt-0.5">
            {{ $t('upgrade.redeem.subtitle') }}
          </p>
        </div>
      </div>

      <!-- Just redeemed -->
      <div
        v-if="redeemedCode"
        class="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-success-800 bg-success-900/30 px-5 py-4"
      >
        <SparklesIcon class="w-5 h-5 text-success-300 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-success-200">{{ $t('upgrade.redeem.redeemed') }}</p>
          <p class="font-mono text-xs text-success-300/80 mt-1">{{ redeemedCode }}</p>
        </div>
        <router-link
          :to="{ name: 'Dashboard' }"
          class="flex-shrink-0 text-sm font-semibold text-success-200 rounded-md px-3 py-1.5 hover:bg-white/10 duration-200"
        >
          {{ $t('upgrade.redeem.go_to_dashboard') }}
        </router-link>
      </div>

      <!-- Active trial notice -->
      <div
        v-else-if="user?.has_active_trial_code"
        class="flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900 px-5 py-4"
      >
        <ClockIcon class="w-5 h-5 text-primary-300 flex-shrink-0" />
        <i18n-t
          scope="global"
          :keypath="activeTrialExpiry ? 'upgrade.redeem.active_trial_until' : 'upgrade.redeem.active_trial'"
          tag="p"
          class="text-sm text-text-secondary"
        >
          <template #date><span class="text-text-primary">{{ activeTrialExpiry }}</span></template>
        </i18n-t>
      </div>

      <!-- Redeem form -->
      <section
        class="rounded-xl border border-primary-800 bg-gray-950 p-6 sm:p-8"
        style="background: radial-gradient(54.79% 79.26% at 51.81% 0%, rgba(78, 84, 200, 0.15) 0%, rgba(78, 84, 200, 0) 100%)"
      >
        <p class="text-xs uppercase tracking-widest text-gray-400 font-bold">{{ $t('upgrade.redeem.enter_code') }}</p>
        <p class="text-sm text-text-secondary mt-2 mb-4">
          {{ $t('upgrade.redeem.start_using') }}
        </p>

        <form class="flex flex-col sm:flex-row gap-3" @submit.prevent="redeemCode(input)">
          <label for="code" class="sr-only">{{ $t('upgrade.redeem.code_label') }}</label>
          <input
            id="code"
            v-model="input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            :placeholder="$t('upgrade.redeem.enter_code_placeholder')"
            class="flex-1 min-w-0 rounded-lg bg-gray-900 border border-gray-800 px-4 py-3 font-mono text-text-primary placeholder-gray-600 focus:border-primary-600 focus:ring-0 focus:outline-none duration-200"
          />
          <button
            type="submit"
            :disabled="!input.trim() || redeeming"
            class="flex-shrink-0 px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
          >
            {{ redeeming ? $t('upgrade.redeem.redeeming') : $t('upgrade.redeem.redeem') }}
          </button>
        </form>

        <p v-if="error" class="flex items-start gap-2 text-sm text-danger-400 mt-3">
          <ExclamationTriangleIcon class="w-4 h-4 flex-shrink-0 mt-0.5" />
          {{ error }}
        </p>
      </section>

      <!-- Your own codes live on their own page -->
      <router-link
        :to="{ name: 'FriendCodesPage' }"
        class="flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900 px-5 py-4 hover:border-gray-700 duration-200 group"
      >
        <GiftIcon class="w-5 h-5 text-gray-400 flex-shrink-0" />
        <i18n-t scope="global" keypath="upgrade.redeem.friend_codes_hint" tag="p" class="flex-1 text-sm text-text-secondary">
          <template #page><span class="text-text-primary">{{ $t('upgrade.redeem.friend_codes_page') }}</span></template>
        </i18n-t>
        <span class="text-sm font-semibold text-primary-300 group-hover:text-primary-200">{{ $t('upgrade.redeem.view') }}</span>
      </router-link>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useHead } from '@unhead/vue';
import axios from 'axios';
import moment from 'moment';
import {
  GiftIcon,
  TicketIcon,
  ClockIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/solid';
import { useI18n } from 'vue-i18n';
import DashboardLayout from '../components/layouts/DashboardLayout.vue';

const { t } = useI18n();
const auth = useAuth();
const route = useRoute();

const user = ref(auth.user());
const input = ref('');
const redeeming = ref(false);
const error = ref(null);
const redeemedCode = ref(null);

const activeTrialExpiry = computed(() => (user.value?.trial_expires_at ? moment(user.value.trial_expires_at).format('LL') : null));

async function redeemCode(rawCode) {
  const code = (rawCode || '').trim();
  if (!code || redeeming.value) return;

  redeeming.value = true;
  error.value = null;

  try {
    await axios.post('/trial_codes/redeem', { code });
    redeemedCode.value = code;
    input.value = '';
    await auth.fetch();
    user.value = auth.user();
  } catch (err) {
    error.value = err.response?.data?.errors?.[0] || t('upgrade.redeem.could_not_redeem');
  } finally {
    redeeming.value = false;
  }
}

useHead({
  title: t('upgrade.redeem.page_title'),
});

onMounted(() => {
  // Codes shared as a link land here with ?code=
  if (route.query.code) input.value = route.query.code;
});
</script>
